import { useLang } from '@/i18n';
import { getSolarRecommand } from '@/service/solar-calculator';
import { FormatMoneyLocal } from '@elem/ui-common/utils/FormatMoneyLocal';
import EcoImg from 'eco-design/base/eco-img';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import css from './index.module.scss';
import { langText } from './langText';
import EcoDesignEventsBus from 'eco-design-micro/EcoDesignEventsBus';
import { ENUM_ECO_CART_EVENT, ItfEcoCartItem } from 'eco-design-components/elements/eco-cart/type.d';
import gtm from 'eco-design-reliance/gtm';

const SectionCard: React.FC = () => {
    const { t } = useLang('d3u.sectionCard');
    const { locale = '' } = useRouter();
    const i18n = langText[locale] || langText['jp'];
    const [list, setList] = useState([]);
    const PRODUCT_CONFIG: any = {
        jp: {
            item1: { productId: '14803254935919', variantId: '52291938976111' },
            item2: { productId: '14803254870383', variantId: '52291943104879' },
        },
    };

    const getProductConfig = (itemName: any) => PRODUCT_CONFIG?.[locale]?.[itemName] || PRODUCT_CONFIG.jp[itemName];
    const items: any[] = [
        {
            title: t('item1_name'),
            texts: [t('item1_text1'), t('item1_text2'), t('item1_text3'), t('item1_text4'), t('item1_text5')],
            productId: getProductConfig('item1').productId,
            variantId: getProductConfig('item1').variantId,
        },
        {
            title: t('item2_name'),
            texts: [t('item2_text1'), t('item2_text2'), t('item2_text3'), t('item2_text4'), t('item2_text5')],
            productId: getProductConfig('item2').productId,
            variantId: getProductConfig('item2').variantId,
        },
    ];

    const getAsynItem = async (productId: string, variantId: string) => {
        return await new Promise(async (resolve, reject) => {
            try {
                const res = await getSolarRecommand({ productId, variantId }, locale);
                if (res?.code === '0') {
                    return resolve(res.data);
                } else {
                    console.error(res?.message);
                    return reject(res?.message);
                }
            } catch (e) {
                console.error(e);
                return reject(e);
            }
        });
    };

    const addToCart = (info: any) => {
        if (Number(info?.inventoryQuantity || 0) > 0 || info?.inventoryPolicy === 'continue') {
            const { compareAtPrice, price, inventoryQuantity, inventoryPolicy, productId, variantId, img, variantImg, productTitle, skuTitle } = info;
            EcoDesignEventsBus.emit(ENUM_ECO_CART_EVENT.ECO_CART_ADD, [
                {
                    compareAtPrice,
                    price,
                    inventoryQuantity,
                    inventoryPolicy,
                    productId,
                    variantId,
                    img,
                    variantImg,
                    productTitle,
                    skuTitle,
                    quantity: 1,
                } as ItfEcoCartItem,
            ]);
            gtm.push({
                event: 'uaEvent',
                event_name: 'bundle_add_to_cart',
                list_name: t('title'),
                product_name: t('title'),
                price,
                tag: 'none',
                quantity: 1,
                button_name: t('right_btn') || i18n.link_cart,
                __btn_id1: 'ef-swiper-card-bundle',
                __btn_id2: t('right_btn') || i18n.link_cart,
                __btn_type: 'common',
            });
        }
    };

    useEffect(() => {
        const promises = items?.map((item: any) => getAsynItem(item?.productId, item.variantId));
        Promise.all(promises).then((res) => {
            const old = JSON.parse(JSON.stringify(items));
            const list: any = res.map((i: any, idx) => {
                old[idx].inventoryQuantity = i?.inventoryPolicy === 'continue' ? 99 : i.inventoryQuantity;
                old[idx].href = `https://${locale}.ecoflow.com/products/${i?.handle}?varaint=${i?.variantId}`;
                old[idx] = { ...old[idx], ...i };
                return old[idx];
            });
            setList(list || []);
        });
    }, []);

    return (
        <section
            className={`${css.section} D3U-Section-Card GtmModuleData-section`}
            id="D3U-Section-Card"
            data-section-id={'D3U-Section-Card'}
            data-section-id2={t('title')}
        >
            <div className="info">
                <p className="title">{t('title')}</p>
            </div>
            <ul className="items">
                {list.map((item: any, idx: number) => (
                    <li className={`item item-${idx + 1}`} key={idx}>
                        <div className="card">
                            <div className="image-col">
                                <div className="image-box" aria-hidden="true">
                                    <EcoImg pc={item?.variantImg || item?.img} />
                                </div>
                            </div>
                            <div className="info-col">
                                <p className="badge">NEW</p>
                                <h3 className="title">{item?.title || item?.skuTItle || item?.productTitle}</h3>
                                <ul className="bullets">
                                    {item.texts.map((txt: any, i: number) => (
                                        <li key={i} className="bullet">
                                            <span className="dot" />
                                            <span className="text">{txt}</span>
                                        </li>
                                    ))}
                                </ul>
                                <div className="price-row">
                                    {item.price && (
                                        <span className="price">
                                            {i18n.price_prefix}
                                            {FormatMoneyLocal(item.price, locale)}
                                            {i18n.price_suffix}
                                        </span>
                                    )}
                                    {item.compareAtPrice && Number(item?.compareAtPrice || 0) !== Number(item?.price || 0) && (
                                        <span className="compare">
                                            {i18n.price_prefix}
                                            {FormatMoneyLocal(item.compareAtPrice, locale)}
                                            {i18n.price_suffix}
                                        </span>
                                    )}
                                </div>
                                <div className="actions">
                                    <a className="btn btn-primary" href={item?.href} target="_blank" rel="noreferrer">
                                        {t('left_btn')}
                                    </a>
                                    <a
                                        className={`btn btn-outline ${Number(item?.inventoryQuantity || 0) > 0 ? 'enable' : 'disable'}`}
                                        onClick={() => addToCart(item)}
                                    >
                                        {t('right_btn')}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
            <div className="tip">{t('tip')}</div>
        </section>
    );
};

export default SectionCard;
